/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { forkJoin, Observable, of, ReplaySubject } from 'rxjs';
import { Field } from '@src/app/shared/field/Field';
import { Omc } from '@src/app/model/Omc';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { FieldSet, LAYOUT_OPTIONS } from '@src/app/shared/form/field-set/field-set.component';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { required } from '@src/app/shared/validators';
import { AgentDataService } from '@src/app/modules/agent/agent-data.service';
import { OmcService } from '@src/app/modules/agent/omc.service';
import { RequestTemplatesService } from '@src/app/modules/crm/request-templates.service';
import { NewButtonService } from '@src/app/shared/new-button/new-button.service';
import { NavOption } from '@src/app/model/sys/navOption';
import { NewRequestData, RequestContextService } from '@src/app/modules/crm/request-context.service';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { UserService } from '@src/app/modules/user/user.service';
import { FormTextAreaComponent } from '@src/app/shared/form/form-text-area/form-text-area.component';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { User } from '@src/app/model/user';
import { RequestTemplate } from '@src/app/model/RequestTemplate';
import { concatMap, first, map } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { CreateNewTeam } from '@src/app/modules/agent/omc-page/createNewTeam';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { AddRecurringJobsAction } from '@src/app/modules/crm/recurring-job/add-recurring-jobs-action';
import { DownloadTemplateAction } from '@src/app/modules/importer/import-page/download-template-action';
import { MONTHLY_TYPE, WEEKLY_TYPE } from '@src/app/modules/crm/request-analysis/request-analysis.component';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { ActivatedRoute, Router } from '@angular/router';
import { PickDialogComponent } from '@src/app/shared/dialogs/pick-dialog/pick-dialog.component';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { FormDateTimeComponent } from '@src/app/shared/form/form-date-time/form-date-time.component';
import { Cycle } from '@src/app/model/cycle';
import { DateHelper } from '@src/app/shared/dateHelper';
import { getLastRecField } from '@src/app/modules/agent/omc-page/lastRecField';
import { FormCheckboxComponent } from '@src/app/shared/form/form-checkbox/form-checkbox.component';
import { makeIsCurrentPillField } from '@src/app/shared/form/form-pill/isCurrentPillField';
import { HomePageComponentComponent } from '@src/app/pages/home-page-component/home-page-component.component';
import { FormNavLinkComponent } from '@src/app/shared/form/form-nav-button/form-nav-link.component';
import { OmcPageNavRoute } from '@src/app/routes/omc-page-nav';
import { BallotPageNavRoute, DocumentPageNavRoute, MailMergeNavRoute } from '@src/app/routes/app-router';
import { UnitPageNavRoute } from '@src/app/routes/unit-page';
import { ActionPageNavRoute } from '@src/app/routes/action-page-nav';
import { RecurringJobPageNavRoute } from '@src/app/routes/recurring-job-page-nav';
import { RequestAnalysisPageNavRoute } from '@src/app/routes/request-analysis-page-nav';
import { PersonPageNavRoute } from '@src/app/routes/person-page-nav';
import { ServiceRequestPageNavRoute } from '@src/app/routes/service-request-page-nav';

@Component({
    selector: 'app-omc-page',
    templateUrl: './omc-page.component.html',
    styleUrls: ['./omc-page.component.scss'],
    imports: [FormPageComponent]
})
export class OmcPageComponent extends AbstractPageComponent {
    dataSvc = inject(OmcService);
    private agentDataSvc = inject(AgentDataService);
    private tmplService = inject(RequestTemplatesService);
    private dialog = inject(MatDialog);
    private reqContextSvc = inject(RequestContextService);
    private activeRoute = inject(ActivatedRoute);
    private cds = inject(ConfirmDialogService);
    private router = inject(Router);
    private nbs = inject(NewButtonService);
    private currentUserSvc = inject(CurrentUserService);

    static defaultView = 'allmyomcs';

    requestTemplates: RequestTemplate[] = [];
    users: User[] = [];

    months = [{id: 1, name: 'January'},{id: 2, name: 'February'},{id: 3, name: 'March'},{id: 4, name: 'April'},
              {id: 5, name: 'May'},{id: 6, name: 'June'},{id: 7, name: 'July'},{id: 8, name: 'August'},
              {id: 9, name: 'September'},{id: 10, name: 'October'},{id: 11, name: 'November'},{id: 12, name: 'December'}
    ]
    days = this.getDays();

    configReady = new ReplaySubject<null>(1);

    addRecurringAction = new AddRecurringJobsAction('omc');

    mineOnlyField = FormPicklistComponent.make('User', 'mineOnlyUserId', 'manager', { items: this.users }, {
        visible: Field.noShow
    });

    mgrField = FormPicklistComponent.make('Manager', 'managerId', 'manager', { items: this.users }, {
        formColumn: 3, validators: [required]
    });

    adminField = FormPicklistComponent.make('Administrator', 'adminId', 'admin',
        { items: this.users, allowSelectNone: true },
        { formColumn: 3, visible: Field.formOnly }
    );

    financeField = FormPicklistComponent.make('Finance', 'financeId', 'finance',
        { items: this.users, allowSelectNone: true },
        { formColumn: 3, visible: Field.formOnly }
    );

    careTakerField = FormPicklistComponent.make('Caretaker', 'caretakerId', 'caretaker',
        { items: this.users, allowSelectNone: true },
        { formColumn: 3, visible: Field.formOnly }
    );

    slugField = FormTextComponent.make('Slug', 'slug', {
        hint: "URL Slug to uniquely identify this team. Note, once team is setup, this cannot be editted. This is also used as the email address for the OMC",
        formColumn: 3
    });

    finYearEndField = FormTextComponent.make('Y/E', 'yearEnd', {
        calculateValue: (omc: Omc) => {
            if (omc.yearEndMonth && omc.yearEndDay && omc.yearEndMonth > 0 && omc.yearEndMonth <= 12) {
                const month = this.months.find( o => o.id === omc.yearEndMonth)?.name;
                return `${month.substring(0,3)}${omc.yearEndDay}`;
            }
            return 'Not Set';
        }, visible: Field.noForm,
    })
    finYearEndDay = FormPicklistComponent.make('Year End Day', 'yearEndDay', null, {items:this.days}, {visible:Field.formOnly});
    finYearEndMonth = FormPicklistComponent.make('Year End Month', 'yearEndMonth', null,
        {items:this.months},
        {formColumn: 1, visible:Field.formOnly}
    );

    doBankingField = FormCheckboxComponent.make('Do you manage the bank account', 'manageBank', {formColumn: 2, visible: Field.formOnly});
    isCurrentField = makeIsCurrentPillField({
        toggle: (o: Omc) => this.confirmCurrentChange(o),
        afterToggle: (o) => this.page.form.reloadContent(o),
        formColumn: 2,
    });

    //FormNumberComponent.make('Recurring Jobs Setup', 'recurring_jobs_count', null, { readonly: true, formColumn: 5}),

    jobsLink = FormNavLinkComponent.make('Jobs',
        RecurringJobPageNavRoute, o => ({ omcId: o.id, _sort: 'nextOccurence' }),
        { formColumn: 4, value: 'recurring_jobs_count', cellOpts: { style: 'text-align: center; max-width: 3em' } }
    );

    requestsLink = FormNavLinkComponent.make('Requests',
        ServiceRequestPageNavRoute, o => ({ omcId: o.id, srStatus: 'open', _sort: 'createdAt' }),
        {
            formColumn: 4, value: 'open_requests_count', cellOpts: {
                style: 'text-align: center; width: 3em', thColspan: 3, thStyle: 'text-align: centre'
            },
            toolTip: $localize `Number of open requests, highlights are if numbers are large relative to number of units`
        },
    ).override({tableStyle: (o: Omc) => {
        const style : Record<string, string> = {
            border:'1px solid', borderRadius: '999px', padding: '0.2em 0.6em', 
            display: 'inline-block'}
        if (o.open_requests_count > Math.max((o.units_count * .3), 5)) {
            style.borderColor = 'red';
            return style;   
        } else if (o.open_requests_count > Math.max((o.units_count * .15), 5)) {
            style.borderColor = 'orange';
            return style;   
        }

        return {}
    }});

    requestsWeeklyLink = FormNavLinkComponent.make('weekly_analysis',
        RequestAnalysisPageNavRoute, o => ({ omcId: o.id, periodType: WEEKLY_TYPE.id }),
        { formColumn: 4,
            calculateValue: () => 'w', cellOpts: { thStyle: 'display: none' }, toolTip: 'Weekly request analysis'
        }
    );

    requestsMonthlyLink = FormNavLinkComponent.make('monthly_analysis',
        RequestAnalysisPageNavRoute, o => ({ omcId: o.id, periodType: MONTHLY_TYPE.id }),
        {
            formColumn: 4,
            calculateValue: () => 'm', cellOpts: { thStyle: 'display: none' }, toolTip: 'Monthly request analysis'
        }
    );


    unitsLink = FormNavLinkComponent.makeForced('Actual Units',
        UnitPageNavRoute, (o: Omc) => o.omcTeamId, null,
        { formColumn: 4, value: 'units_count', cellOpts: { style: 'text-align: center; max-width: 3em' } }
    );

    peopleLink = FormNavLinkComponent.makeForced('People',
        PersonPageNavRoute, (o: Omc) => o.omcTeamId, null,
        { formColumn: 4, value: 'people_count', cellOpts: { style: 'text-align: center; max-width: 3em' } }
    );

    documentLink = FormNavLinkComponent.makeForced('Docs',
        DocumentPageNavRoute, (o: Omc) => o.omcTeamId, null,
        { formColumn: 4, value: 'documents_count', cellOpts: { style: 'text-align: center; max-width: 3em' } }
    );

    todoLink = FormNavLinkComponent.make('Actions',
        ActionPageNavRoute, o => ({ omcId: o.id, completedAt: '', _sort: 'plannedDate' }),
        { formColumn: 4, value: 'todo_actions_count', cellOpts: { style: 'text-align: center; max-width: 3em' } }
    );

    ballotLink = FormNavLinkComponent.makeForced('Ballots',
        BallotPageNavRoute, (o: Omc) => o.omcTeamId,  () => ({_sortDesc: 'closesAt' }),
        { formColumn: 4, value: 'ballots_count', cellOpts: { style: 'text-align: center; max-width: 3em' } }
    );
    mergesLink = FormNavLinkComponent.makeForced('Merges',
        MailMergeNavRoute, (o: Omc) => o.omcTeamId, null,
        { formColumn: 4, value: 'mail_merges_count', cellOpts: { style: 'text-align: center; max-width: 3em' } }
    );

    typeField = FormPicklistComponent.make('Type', 'typeId', null, { items: Omc.omcTypes }, { 
        validators: [required], visible:Field.formOnly
    });

    lastRecField = getLastRecField();
    
    lastPullField = FormDateTimeComponent.make('Last Pull', 'gocLastPull', {
        readonly: true, formColumn: 4, disable: true, condensed: true, 
        toolTip: $localize `
            The date the system last successfully pulled transactions for this account.
            Shows in red if more than 48 hours since the last transactions collected.
            Transactions should be collected every 12 hours, so after 48 hours
            without a collection there must be a problem with the bank authorisation. 
            If shown empty, the bank account is not connected.
        `,
        tableStyle: (o: Omc) => {
            const secondsAgo = DateHelper.secondsFromToday(o.gocLastPull);
            if (secondsAgo < 0 - (48 * 60 * 60)) {
                return {color: 'red'}
            }
            return {};
        }
    })

    budgetStatusField = FormPicklistComponent.make('Budget Status', 'cycleStatusId', 'status', 
        { items: Cycle.statuses }, { readonly: true, visible: Field.noForm,
        toolTip: $localize `Budget status for the current cycle - Have you billed out yet`,
        tableStyle: (o: Omc) => {
            if (o.cycleStatusId === Cycle.statusIds.BILLED) {
                return {color: 'green'}
            }
            return {};
        }        
    });

    cashDaysField = FormNumberComponent.make('Days Cash', 'daysCash', {formatParms: '1.0-0'}, {
        formColumn: 4, condensed: true,
        calculateValue: (o:Omc) => {
            if (o.budgetTotal) {
                return +o.balance / +o.budgetTotal * 365;
            }
            return 0;
        }, cellOpts: {style: 'max-width: 4em, text-align: right'},
        toolTip: $localize `
            Cash days is a very approximate heuristic. 
            It incorrectly assumes, that your total budget is spent evenly every day.
            If your budget is €36,500 it assumes that you spend €100 per day.
            It then calculates how many days cash you have in the bank.
            Uses actual balance for connected accounts, or last reconciled balance for unconnected accounts.
            Color coding is green > 90 days, orange 60-90 days, red 30-60 days, red background less than 30 days.
        `,
        tableStyle: (o: Omc) => {
            if (o.budgetTotal) {
                const days =  +o.balance / +o.budgetTotal * 365;
                if (days > 90) {
                    return {color: 'green', textAlign: 'center'}
                } else if (days > 60) {
                    return {color: 'orange', textAlign: 'center'}
                } else if (days > 30) {
                    return {color: 'red', textAlign: 'center'}
                } else {
                    return {color: 'white', backgroundColor: 'red', borderRadius: '3px', padding: '2px', textAlign: 'center'}
                }
            }
            return {};
        }
    })

    nameField = FormNavLinkComponent.makeForced('Name', 
        HomePageComponentComponent.navRoute, 
        (o:Omc)=> o.omcTeamId, 
        null,
        {value: 'name', name: 'omcName', label: 'OMC', formColumn: 4}
    );
    nameTextField = FormTextComponent.make('Name', 'name', {visible: Field.formOnly});

    config = new FormConfig({
        navRoute: OmcPageNavRoute,
        docPath: 'omc-professional/omc-list',
        title: $localize`Owners\' Management Company`,
        help: $localize`The Owner Management Companies (OMCs) an agent does business with`,
        fieldSet: new FieldSet({
            fields: [
                FieldMaker.id(),
                FieldMaker.rev(),
                this.mineOnlyField,
                this.nameField,
                this.nameTextField,
                this.finYearEndField,
                this.lastRecField,
                this.lastPullField,
                this.budgetStatusField,
                this.cashDaysField,
                this.typeField,
                //                FormTextComponent.make('Manager', 'manager.name', { readonly: true, visible: Field.noForm, sendServer:false }),
                FormTextComponent.make('Legal Name', 'legalName', { formColumn: 1, visible: Field.formOnly }),
                FormTextAreaComponent.make('Default Address', 'address',
                    { formColumn: 2,
                        visible: Field.formOnly,
                        hint: 'Default address to add to the end of all unit addresses'
                    }),
                FieldMaker.notes({ formColumn: 2, visible: Field.formOnly }),
                this.doBankingField,
                this.isCurrentField,
                this.requestsLink,
                this.requestsWeeklyLink,
                this.requestsMonthlyLink,
                this.todoLink,
                this.jobsLink,                
                this.ballotLink,
                this.mergesLink,               
                FormNumberComponent.make('Expected Units', 'expectedUnits', null,
                    { formColumn: 1, validators: [required], 
                        cellOpts: { style: 'text-align: center; max-width: 3em' }, visible: Field.formOnly }),
                //FormNumberComponent.make('Actual Units', 'units_count', null, { formColumn: 5, readonly: true }),
                this.finYearEndMonth,
                this.finYearEndDay,
                this.unitsLink,
                this.peopleLink,
                this.documentLink,
                this.mgrField,
                this.adminField,
                this.financeField,
                this.careTakerField,
                this.slugField,
            ],
            formLayout: [{ cells: [{ width: '10%' }, { width: '10%' }, { width: '10%' }, { width: '10%' }, { width: '1%' }] }]
        }),
        service: this.dataSvc,
        mode: 'list',
        objectFactory: this.newOMC.bind(this),
        configReady: this.configReady,
        beforeEdit: this.beforeEdit.bind(this),
        afterSave: () => this.agentDataSvc.clearOmcList(),
        actions: [this.addRecurringAction, new DownloadTemplateAction(), new CreateNewTeam()],
        newOptions: [
            { name: $localize`New OMC`, basePath: OmcPageNavRoute.newUrl, id: 'newOmc' },
            { name: $localize`Accept invite from OMC`, basePath: OmcPageNavRoute.newUrl, params: {addExisting: 1}, id: 'acceptInvite'    }
        ]
    });

    private confirmCurrentChange(o: Omc): Observable<Omc | null> {
        const title = o.isCurrent ? $localize `Archive OMC ${o.name}` : $localize `Reactivate OMC ${o.name}`;
        const msg = o.isCurrent
            ? $localize `Mark this OMC as archived, you no longer do business with them?`
            : $localize `Unarchive this OMC, so you can create requests and it appear in your OMC list by default?`;
        return this.cds.openConfirm(title, msg).pipe(concatMap(answer => answer
            ? this.dataSvc.put({ id: o.id, revision: o.revision, isCurrent: !o.isCurrent })
            : of(null)));
    }

    constructor() {
        const userSvc = inject(UserService);

        super();
        const tmplService = this.tmplService;
        const currentUserSvc = this.currentUserSvc;

        this.subscriptions.push(forkJoin([
            tmplService.get(true).pipe(first()),
            userSvc.getUsers().pipe(first()),
            currentUserSvc.getCurrentUser().pipe(first())
        ]).subscribe(
            ([templates, users, currentUser]) => {
                this.requestTemplates = templates as RequestTemplate[];
                this.users = users as User[];
                this.mgrField.setPicklistItems(this.users);
                this.adminField.setPicklistItems(this.users);
                this.financeField.setPicklistItems(this.users);
                this.careTakerField.setPicklistItems(this.users);

                this.currentUser = currentUser as User;
                this.configReady.next(null);
            }
        ));
    }

    getDays() {
        const days = []
        for (let i = 1; i <= 31; i++) {
            days.push({id: i, name: ("0" + i).slice(-2)})
         }
        return days;
    }

    newOMC() {
        if (this.activeRoute?.snapshot?.queryParams?.addExisting) {
            console.log('Add Existing');
            return this.chooseExisting();
        }
        this.isCurrentField.visible = Field.noShow;
        this.slugField.enableControl();
        this.slugField.sendServer = true;
        return of(new Omc());
    }

    chooseExisting() {
        const config = new FormConfig({
            fieldSet: new FieldSet({
                fields: [
                    FormTextComponent.make('slug', 'id'),
                    FormTextComponent.make('Name', 'name'),
                    FormTextComponent.make('Legal Name', 'legalName'),
                ],
                formLayout: LAYOUT_OPTIONS.singleCol,
            }),
            title: $localize`OMCs Invitations to share`,
            mode: 'list',
        });

        const dialogRef = this.dialog.open(PickDialogComponent,
            {
                data: {
                    config: config,
                    service: {
                        get: this.dataSvc.getInvites.bind(this.dataSvc)
                    },
                }
        });
        return dialogRef.afterClosed().pipe( concatMap ( (omcTeam) => {
            if (omcTeam) {
                const title = $localize `Link OMC to your agency`;
                const msg = $localize `Do you want to accept invitation from ${omcTeam.name} and be able to share their data?`
                const choice = {
                    title,
                    msg,
                    options: [{ name: 'OK', returns: true }]
                }
                return this.cds.openChoice(choice).pipe( concatMap( confirmed => {
                    if (confirmed) {
                        return this.dataSvc.acceptInvites(omcTeam).pipe(map( omc => {
                            if (omc) {
                                this.router.navigate([OmcPageNavRoute.getIdUrl(omc.id)]);
                            }
                        }));
                    } else {
                        this.router.navigate([OmcPageNavRoute.getListRouteName()]);
                    }
                }));
            } else {
                this.router.navigate([OmcPageNavRoute.getListRouteName()]);
            }
        }));
    }

    /**
     * Raised for the OMC whose page this is - so naming its team settles the tenancy rather than
     * asking which OMC, the way the requests page has to.
     */
    private newOmcRequest(omc: Omc, data: NewRequestData) {
        this.reqContextSvc.newRequest({ ...data, teamId: omc.omcTeamId }).subscribe(created => {
            if (created) {
                this.router.navigate([ServiceRequestPageNavRoute.getIdUrl(created.id)]);
            }
        });
    }

    beforeEdit(omc: Omc) {

        this.isCurrentField.visible = Field.formOnly;
        if (omc) {
            if (this.reqContextSvc.canCreate()) {
                const newOptions: NavOption[] = [
                    { name: $localize`New Case`, id: 'newCase',
                        action: () => this.newOmcRequest(omc, { requestType: 'case' }) },
                    { name: $localize`New Work Order`, id: 'newWorkOrder',
                        action: () => this.newOmcRequest(omc, { requestType: 'workOrder' }) },
                    { name: $localize`New Standard Request`, id: 'newTemplatedRequest',
                        action: () => this.newOmcRequest(omc, { requestType: 'case', templated: true }) }
                ];

                this.nbs.setupButton($localize`Request For ${omc.name}`, '', true, newOptions);
            }
        } else {
            omc = new Omc(); //avoid errors if no omc, e.g. not logged in...
        }
        if (omc.omcTeamId) {
            this.slugField.disableControl();
            this.slugField.sendServer = false;
        } else {
            this.slugField.enableControl();
            this.slugField.sendServer = true;
        }
        return omc;
    }
}
